import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import axios from 'axios'
import { User } from '../types/User'
import { usersApi } from '../client/API'
import { useSnackbar } from './snackbar-context'
import {
  setTokenGetter,
  clearTokenGetter,
  isTokenGetterReady,
} from '../axios/token-manager'
import {
  setSessionExpiredHandler,
  clearSessionExpiredHandler,
} from '../axios/session-expired'

interface AuthContextType {
  isAuthenticated: boolean
  // True only once auth has fully settled: Auth0 done loading, authenticated,
  // AND the token getter is wired up. On-mount authenticated fetches should
  // gate on THIS, not isAuthenticated, to avoid firing before a token exists.
  isAuthReady: boolean
  isLoading: boolean
  user: User | null
  // Auth0 session belongs to a soft-deleted account (API answers 403).
  isDeletedAccount: boolean
  loginWithPopup: () => void
  logout: () => void
  getAccessToken: () => Promise<string>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const USER_STORAGE_KEY = 'movieception_user'
const SETTINGS_CACHE_KEY = 'settings-cache'
const MEDIA_STATE_CACHE_KEY = 'media-state-cache'
// Auth0 logout can only return to whitelisted URLs (the origin), so the page
// the user was on is stashed here and restored after the redirect lands.
export const POST_LOGOUT_PATH_KEY = 'post-logout-path'
// Pages that require auth - after logout from these we go home instead
const AUTH_REQUIRED_PATH_PREFIX = '/profile'

const clearAllStorage = () => {
  localStorage.removeItem(USER_STORAGE_KEY)
  // Settings are cached in localStorage (use-settings); clear both to be safe
  localStorage.removeItem(SETTINGS_CACHE_KEY)
  sessionStorage.removeItem(SETTINGS_CACHE_KEY)
  sessionStorage.removeItem(MEDIA_STATE_CACHE_KEY)
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithPopup,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0()

  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  })
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [isDeletedAccount, setIsDeletedAccount] = useState(false)
  // Flips true only after setTokenGetter has run (see the effect below), so
  // consumers can wait for a usable token, not just an Auth0 session.
  const [tokenGetterReady, setTokenGetterReady] = useState(isTokenGetterReady())
  const { enqueueSnackbar } = useSnackbar()

  const getAccessToken = async () => {
    return await getAccessTokenSilently()
  }

  // Proactively renew the access token so an active user is never bounced at
  // expiry. This talks to Auth0 (swaps the refresh token for a fresh access
  // token) - there is no keep-alive endpoint on our own API. On failure the
  // existing session-expired handler / login redirect takes over.
  const keepAlive = async () => {
    try {
      await getAccessTokenSilently()
    } catch {
      // refresh token expired or revoked - dead-session recovery handles it
    }
  }

  // Fire keepAlive when the tab regains focus and on a long interval (shorter
  // than the ~24h access-token lifetime) so the token is refreshed before it
  // lapses.
  useEffect(() => {
    if (!isAuthenticated) return

    const onVisible = () => {
      if (document.visibilityState === 'visible') keepAlive()
    }
    document.addEventListener('visibilitychange', onVisible)
    // Refresh access token every 20 hours to prevent token expiration during active sessions
    const intervalId = window.setInterval(keepAlive, 20 * 60 * 60 * 1000)

    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // Set up token getter for axios interceptor when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(getAccessTokenSilently)
      setTokenGetterReady(true)
    } else {
      clearTokenGetter()
      setTokenGetterReady(false)
    }

    return () => {
      clearTokenGetter()
    }
  }, [isAuthenticated, getAccessTokenSilently])

  // Dead-session recovery: the app looks logged in but token renewal fails
  // and the API answers 401 (revoked/expired refresh token, wiped storage).
  // Stash the current page (the same mechanism logout uses), clear the stale
  // caches, and bounce through Auth0 - RestorePostLogoutPath brings the user
  // straight back after the redirect lands.
  useEffect(() => {
    if (!isAuthenticated) {
      clearSessionExpiredHandler()
      return
    }

    setSessionExpiredHandler(() => {
      clearAllStorage()
      setUser(null)
      const path = window.location.pathname + window.location.search
      sessionStorage.setItem(POST_LOGOUT_PATH_KEY, path)
      enqueueSnackbar('Your session expired - signing you back in', {
        variant: 'info',
      })
      loginWithRedirect()
    })

    return () => {
      clearSessionExpiredHandler()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loginWithRedirect])

  const fetchUser = async () => {
    setIsLoadingUser(true)
    try {
      const token = await getAccessTokenSilently()
      const response = await usersApi.getCurrentUser(token)
      setUser(response.data)
      setIsDeletedAccount(false)
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.data))
      // A real authenticated call succeeded: clear the relogin loop guard so a
      // genuine future expiry can still trigger recovery.
      try {
        sessionStorage.removeItem('auth-relogin-attempt')
      } catch {
        /* best effort */
      }
    } catch (error) {
      const isNotFoundError =
        axios.isAxiosError(error) && error.response?.status === 404
      const isDisabledAccount =
        axios.isAxiosError(error) && error.response?.status === 403
      if (isNotFoundError) {
        await registerUser()
      } else if (isDisabledAccount) {
        // Blocking modal (rendered in application.tsx) takes it from here: it
        // explains the situation and offers logout so another account can sign in.
        setUser(null)
        localStorage.removeItem(USER_STORAGE_KEY)
        setIsDeletedAccount(true)
      } else {
        console.error('Error fetching user:', error)
        setUser(null)
        localStorage.removeItem(USER_STORAGE_KEY)
        enqueueSnackbar('Login failed. Please try again.', { variant: 'error' })
      }
    } finally {
      setIsLoadingUser(false)
    }
  }

  // Region from the browser locale (e.g. tr-TR -> TR); the server falls back
  // to US when we can't tell. Users can change it later in Settings.
  const detectRegion = (): string | undefined => {
    try {
      const locale =
        navigator.languages?.find((l) => l.includes('-')) ?? navigator.language
      const region = locale?.split('-').pop()?.toUpperCase()
      return region && /^[A-Z]{2}$/.test(region) ? region : undefined
    } catch {
      return undefined
    }
  }

  const registerUser = async () => {
    try {
      const token = await getAccessTokenSilently()
      const response = await usersApi.register(token, {
        name: auth0User?.name || '',
        emailAddress: auth0User?.email || '',
        profilePictureUrl: auth0User?.picture,
        isSubscribedToEmailNotifications: true,
        region: detectRegion(),
      })
      setUser(response.data)
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.data))
    } catch (error) {
      console.error('Error registering user:', error)
      setUser(null)
      localStorage.removeItem(USER_STORAGE_KEY)
      enqueueSnackbar('Login failed. Please try again.', { variant: 'error' })
    }
  }

  const refreshUser = async () => {
    await fetchUser()
  }

  const logout = () => {
    setUser(null)
    setIsDeletedAccount(false)
    clearAllStorage()
    const path = window.location.pathname + window.location.search
    if (!path.startsWith(AUTH_REQUIRED_PATH_PREFIX)) {
      sessionStorage.setItem(POST_LOGOUT_PATH_KEY, path)
    }
    auth0Logout({ logoutParams: { returnTo: window.location.origin } })
    enqueueSnackbar('Logged out', { variant: 'info' })
  }

  useEffect(() => {
    if (auth0Loading) return

    if (isAuthenticated && !user) {
      fetchUser()
    }
  }, [isAuthenticated, auth0Loading, user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated && !auth0Loading) {
      setUser(null)
      clearAllStorage()
    }
  }, [isAuthenticated, auth0Loading])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthReady: isAuthenticated && !auth0Loading && tokenGetterReady,
        isLoading: auth0Loading || isLoadingUser,
        user,
        isDeletedAccount,
        loginWithPopup,
        logout,
        getAccessToken,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
